// src/context/XPContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import AchievementPopups from '../components/AchievementPopups';

const XPContext = createContext();

export const XPProvider = ({ children }) => {
  const [earnedXP, setEarnedXP] = useState(null);
  const [showXPPopup, setShowXPPopup] = useState(false);
  const [showLevelPopup, setShowLevelPopup] = useState(false);
  const [levelValue, setLevelValue] = useState(null);
  const [pendingLevelUp, setPendingLevelUp] = useState(null);
  const [xpActivityType, setXpActivityType] = useState('default'); // Track activity type

  // Function to check current level from backend
  const checkCurrentLevel = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await fetch('https://dev-api.curu.com.au/api/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) return null;
      
      const data = await response.json();
      return data.level?.id || null;
    } catch (error) {
      console.error('Error fetching current level:', error);
      return null;
    }
  };

  // Check for earnedXP and level-ups
  useEffect(() => {
    const checkForEarnedXP = async () => {
      console.log("XPContext: Checking for earnedXP and level changes");
      
      const xpValue = localStorage.getItem('earnedXP');
      const activityType = localStorage.getItem('xpActivityType') || 'default';
      console.log("XPContext: Found XP value:", xpValue, "Activity type:", activityType);
      
      if (xpValue) {
        console.log("XPContext: Setting XP popup with value:", xpValue);
        setEarnedXP(parseInt(xpValue));
        setXpActivityType(activityType);
        setShowXPPopup(true);
        localStorage.removeItem('earnedXP');
        localStorage.removeItem('xpActivityType');
        console.log("XPContext: Cleared earnedXP from localStorage");
        
        const currentLevel = await checkCurrentLevel();
        const storedPreviousLevel = localStorage.getItem('userLevel');
        
        if (currentLevel && storedPreviousLevel) {
          const prevLevel = parseInt(storedPreviousLevel);
          if (currentLevel > prevLevel) {
            console.log("XPContext: Level up detected! Storing for after XP popup");
            setPendingLevelUp(currentLevel);
          }
        }
        
        // Update stored level
        if (currentLevel) {
          localStorage.setItem('userLevel', currentLevel.toString());
        }
      }

      const levelUpValue = localStorage.getItem('levelUp');
      if (levelUpValue) {
        console.log("XPContext: Direct level-up detected:", levelUpValue);
        
        if (!showXPPopup) {
          console.log("XPContext: Showing level popup immediately");
          setLevelValue(parseInt(levelUpValue));
          setShowLevelPopup(true);
        } else {
          // If XP popup is showing, store for later
          console.log("XPContext: Storing level-up for after XP popup");
          setPendingLevelUp(parseInt(levelUpValue));
        }
        
        localStorage.removeItem('levelUp');
      }
    };

    checkForEarnedXP();
    
    const handleCustomXPEvent = () => {
      console.log("XPContext: Received check-xp-earned event");
      checkForEarnedXP();
    };
    
    const handleCustomLevelEvent = () => {
      console.log("XPContext: Received check-level-up event");
      checkForEarnedXP();
    };
    
    window.addEventListener('check-xp-earned', handleCustomXPEvent);
    window.addEventListener('check-level-up', handleCustomLevelEvent);
    window.addEventListener('storage', checkForEarnedXP);
    
    return () => {
      window.removeEventListener('check-xp-earned', handleCustomXPEvent);
      window.removeEventListener('check-level-up', handleCustomLevelEvent);
      window.removeEventListener('storage', checkForEarnedXP);
    };
  }, [showXPPopup]);

  const closeXPPopup = () => {
    setShowXPPopup(false);
    setEarnedXP(null);
    setXpActivityType('default');
    
    if (pendingLevelUp) {
      console.log("XPContext: Showing level popup after XP popup closed");
      setLevelValue(pendingLevelUp);
      setShowLevelPopup(true);
      setPendingLevelUp(null);
    }
  };

  const closeLevelPopup = () => {
    setShowLevelPopup(false);
    setLevelValue(null);
  };

  return (
    <XPContext.Provider value={{ 
      earnedXP, 
      showXPPopup, 
      closeXPPopup, 
      showLevelPopup, 
      levelValue, 
      closeLevelPopup 
    }}>
      {children}
      
      {showXPPopup && (
        <AchievementPopups
          type="xp"
          value={earnedXP}
          activityType={xpActivityType}
          onClose={closeXPPopup}
        />
      )}
      
      {showLevelPopup && (
        <AchievementPopups
          type="level"
          value={levelValue}
          onClose={closeLevelPopup}
        />
      )}
    </XPContext.Provider>
  );
};

export const useXP = () => useContext(XPContext);